import {FigureMainClass} from "./figureMainClass";
import {MapPosition} from "../../models/MapPosition";
import {from, Observable, Subscriber} from "rxjs";
import {ObjectConfig} from "../../models/ObjectConfig";
import {debounceTime} from "rxjs/operators";

export class DynamicBillboardPrimitive extends FigureMainClass{
  private _position : MapPosition;
  private _rotation : number;
  private _fixedWidth: number;
  private _fixedHeight: number;
  private _fixedScale: number;
  private _mapIcon: string;

  private additionalRotateBySceneRotation: number = 0;

  private updateLinkedObjectsAnchorPosition$ = new Observable((observer)=>{
    this.updateLinkedObjectsAnchorPosition();
    observer.next();
  });

  get position() { return this._position; }
  set position(val) {
    if (val.lat === null || val.lng === null) {
      console.error("Illegal position for object", this.object, val);
      alert("Illegal position. Saving denied.\nPlease, reload the page.");
      return;
    }
    this._position = val;
    // note: [SHR]: difference
    this.updateLinkedObjectsAnchorPosition$.pipe(debounceTime(300)).subscribe(()=>{
      this.setObjectConfigValue('position', this._position);
    })
  }

  moveByOffsetFromOriginalPosition(offset){
    let pos = Object.assign({}, this.position);
    pos = new MapPosition({lat: pos.latitude+offset.latitude, lng: pos.longitude+offset.longitude});
    this.lastDragPosition = pos;
    this.view.moveBillboardPrimitive(this.viewEntityId, pos);
  }


  get mapIcon() { return this._mapIcon; }
  get initialConfig() { return this.getInitialConfig(); }

  get rotation() { return this._rotation; }
  set rotation(val) {
    this._rotation = val;
    this.setObjectConfigValue('rotation', this._rotation);
  }

  get finalWidth(){ return this._fixedWidth; }
  get finalHeight(){ return this._fixedHeight; }
  get finalScale(){ return this._fixedScale; }
  get finalSize(){return {width: this._fixedWidth, height: this._fixedHeight}}

  get resultRotation() {
    return this._rotation - this.additionalRotateBySceneRotation;
  }

  protected __init(config) {
    this._position = config.getPosition();
    this._rotation = config.getRotation();
    this._fixedScale = config.getFixedScale();
    this._fixedWidth = config.getFixedWidth();
    this._fixedHeight = config.getFixedHeight();

    this._mapIcon = config.getMapIcon();

    this.initSelfListeners();
  }

  private initSelfListeners(){
    /*this.view.sceneRotated$.subscribe((degrees) => {
      if (this.viewEntityId) {
        this.additionalRotateBySceneRotation = degrees;
        this.view.rotateBillboardPrimitive(this.viewEntityId, this.resultRotation);
      }
    });*/

    //TODO: need to fix sceneRotated$ event, subsribe on it and delete this code
    this.view.viewer.scene.postRender.addEventListener(()=>{
      if (this.viewEntityId) {
        const sceneRotatedDegrees = Cesium.Math.toDegrees(this.view.getCameraOrientation().heading);
        if (Math.abs(this.additionalRotateBySceneRotation - sceneRotatedDegrees) > 0.01) {
          this.additionalRotateBySceneRotation = sceneRotatedDegrees;
          this.view.rotateBillboardPrimitive(this.viewEntityId, this.resultRotation);
        }
      }
    });
  }

  public setGrayScale(value){
    return;
  }

  protected __onInternalUpdate(config){
    this.updateFigureSizeFromConfig(config);
    this.updateMapIcon(config);
    if (!this._position.equals(config.getPosition())) {
      this._position = config.getPosition();
      this.updatePosition();
      this.updateLinkedObjectsAnchorPosition();
    }
    this.view.viewer.scene.requestRender();
  }

  protected __onServerUpdate(config) {
    if (!this._position.equals(config.getPosition())) {
      this._position = config.getPosition();
      this.updatePosition();
    }

    if (this._rotation != config.getRotation()) {
      this._rotation = config.getRotation();
      this.view.rotateBillboardPrimitive(this.viewEntityId, this.resultRotation);
    }

    this.updateFigureSizeFromConfig(config);
    this.updateMapIcon(config);
    this.view.viewer.scene.requestRender();
  }

  private updatePosition(){
    if (this.viewEntityId) {
      this.view.moveBillboardPrimitive(this.viewEntityId, this.position)
    }
  }

  private updateMapIcon(config: ObjectConfig) {
    if (config.getMapIcon() != this._mapIcon) {
      this._mapIcon = config.getMapIcon();
      if (this.viewEntityId) {
        this.view.changeBillboardPrimitiveMapIcon(this.viewEntityId, this.mapIcon);
      }
    }
  }

  public forceResize(config: ObjectConfig, additionalOptions?){
    if (this.viewEntityId) {
      this.view.changeBillboardPrimitiveFixedSize(this.viewEntityId, this.finalScale, this.finalWidth, this.finalHeight, this.getInitialConfig(), additionalOptions);
    }
  }

  protected updateFigureSizeFromConfig(config:ObjectConfig){
    if (this._fixedWidth != config.getFixedWidth() || this._fixedHeight != config.getFixedHeight() || this._fixedScale != config.getFixedScale()){
      this._fixedScale = config.getFixedScale();
      this._fixedWidth = config.getFixedWidth();
      this._fixedHeight = config.getFixedHeight();

      if (this.viewEntityId) {
        this.view.changeBillboardPrimitiveFixedSize(this.viewEntityId, this.finalScale, this.finalWidth, this.finalHeight, this.getInitialConfig());
      } else {
        //todo: check zoom config when viewerEntityId will be in Promise then
        let self = this;
        setTimeout(()=>{
          self.updateFigureSizeFromConfig(config);
        }, 500);
      }
    }
  }

  public draw() {
    return from(new Observable((observer)=>{
      if (this.view.sceneRotatedDegrees !== 0) {
        // this.additionalRotateBySceneRotation = this.view.sceneRotatedDegrees;
        const sceneRotatedDegrees = Cesium.Math.toDegrees(this.view.getCameraOrientation().heading);
        this.additionalRotateBySceneRotation = sceneRotatedDegrees;
      }

      this.view.drawDynamicBillboardPrimitive(
        this.tool,
        this,
        (primitiveId) => {
          this.setPrimitiveId(primitiveId, observer);
        }
      );
    }));
  }

  public removeObject(){
    if (this.viewEntityId) {
      this.view.removeObject(this.viewEntityId);
    }
  }

  private setPrimitiveId(primitiveId, observer:Subscriber<any>){
    this.setViewEntityId(primitiveId);
    observer.next(primitiveId);
  }

  public getRotation(){
    return this.rotation;
  }

  public rotateTo(degrees){
    return new Promise((resolve)=>{
      this.rotation=degrees;
      if (this.viewEntityId) {
        this.view.rotateBillboardPrimitive(this.viewEntityId, this.resultRotation, resolve)
      }
    })
  }

  public rotate(degrees){
    return new Promise((resolve)=>{
      this.rotation+=degrees;
      this.view.rotateBillboardPrimitive(this.viewEntityId, this.resultRotation, resolve)
    })
  }

  public moveBy(offset) {
    let pos = Object.assign({}, this.position);

    this.position = new MapPosition({lat: pos.latitude+offset.latitude, lng: pos.longitude+offset.longitude});
    this.view.moveBillboardPrimitive(this.viewEntityId, this.position);
  }

  updateObjectPositionByMultiSelect() {
    if (this.lastDragPosition) {
      this.position = this.lastDragPosition;

      this.lastDragPosition = null;
    }
  }

  public moveWithDirection(event){
    let newPos = this.view.getPositionAfterDirectionOffset(this.position, event);
    this.position = newPos;
    if (this.viewEntityId) {
      this.view.moveBillboardPrimitive(this.viewEntityId, newPos);
    }
  }


  getLinkedObjectsAnchorPosition(){
    return this.position;
  }

  public onDragEnd(object: {position, id}){
    this._dragEnd.next({oldPosition: null, object:object});
    this.position = object.position;
  }

  public onRotateStart(){
    if (this.viewEntityId) {
      this.view.enableObjectRotate(this.viewEntityId, this.position, this.rotation, (degrees)=>{this.rotation = degrees;})
    }
  }

  public setTransparency(value){
    if (this.viewEntityId) {
      this.view.setObjectTransparency(this.viewEntityId, true);
    }
  }

  public centerOnObject(offsetHeight){
    if (this.viewEntityId) {
      this.view.centerOnPrimitive(this.viewEntityId, this.position, offsetHeight);
    }
  }

}
